import React from 'react';
import { XIcon, VideoIcon, FileTextIcon, ExternalLinkIcon } from 'lucide-react';
import { Asset } from '../pages/AssetsManagement';

interface AssetPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: Asset;
  formatFileSize: (bytes: number) => string;
}

const AssetPreviewModal: React.FC<AssetPreviewModalProps> = ({
  isOpen,
  onClose,
  asset,
}) => {
  if (!isOpen) return null;

  const renderContent = () => {
    switch (asset.type) {
      case 'image':
        return (
          <img
            src={asset.thumbnailUrl || asset.url}
            alt={asset.name}
            className="max-w-full max-h-[80vh] object-contain rounded"
          />
        );

      case 'video':
        return (
          <div className="bg-gray-900 rounded p-8 text-center">
            {asset.thumbnailUrl ? (
              <div className="relative inline-block">
                <img
                  src={asset.thumbnailUrl}
                  alt={asset.name}
                  className="max-w-full max-h-[60vh] object-contain rounded"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                    <VideoIcon className="h-8 w-8 text-gray-700 ml-1" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-16">
                <VideoIcon className="h-16 w-16 mx-auto mb-4 text-white opacity-75" />
                <p className="text-white opacity-75 mb-4">Video Preview</p>
              </div>
            )}
            <a
              href={asset.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors text-sm"
            >
              <ExternalLinkIcon className="h-4 w-4 mr-2" />
              Open Video
            </a>
          </div>
        );

      case 'pdf':
        return (
          <div className="bg-white rounded p-12 text-center">
            <div className="w-20 h-20 bg-red-100 rounded flex items-center justify-center mx-auto mb-4">
              <FileTextIcon className="h-10 w-10 text-red-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{asset.name}</h3>
            <p className="text-sm text-gray-600 mb-4">Click below to open this PDF document</p>
            <a
              href={asset.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
            >
              <ExternalLinkIcon className="h-4 w-4 mr-2" />
              Open PDF
            </a>
          </div>
        );

      default:
        return (
          <div className="bg-white rounded p-12 text-center">
            <FileTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Preview not available for this file type</p>
          </div>
        );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-80" />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-gray-800 bg-opacity-70 hover:bg-opacity-100 text-white rounded-full transition-all"
      >
        <XIcon className="h-6 w-6" />
      </button>

      {/* Content */}
      <div
        className="relative z-10"
        onClick={(e) => e.stopPropagation()}
      >
        {renderContent()}
      </div>
    </div>
  );
};

export default AssetPreviewModal;
